import { InfoOutlined } from "@mui/icons-material";
import { Box, Collapse, type SxProps, type Theme, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { ElementType, FC, ReactNode } from "react";
import { ALPHA, FONT_SIZE, MOTION } from "../../../theme/tokens";

/** Semantic tone. Maps onto the theme's status palette — never a hardcoded hex. */
export type InlineHintTone = "info" | "warning" | "success" | "neutral";

type Props = {
	/** The message, already translated — the caller owns i18n (same contract as `EmptyState`'s `title`). */
	children: ReactNode;
	/**
	 * Element the message renders as. Default `"p"`. Pass `"div"` when the message is itself block-level
	 * markup (a `<ul>` of reasons, a stacked label + list) — a list inside a `<p>` is invalid nesting and
	 * the browser silently closes the paragraph around it, which breaks the hint's layout. Same contract
	 * and same trap as `SystemBanner`'s `descriptionComponent`.
	 */
	contentComponent?: ElementType;
	tone?: InlineHintTone;
	/** Override the leading glyph so the hint reads as its meaning. */
	icon?: ReactNode;
	/** Drop the glyph entirely, for very narrow columns where it would crowd the text. */
	hideIcon?: boolean;
	/** Action slot, e.g. a borderless `SystemButton`. */
	action?: ReactNode;
	/**
	 * Where `action` sits. `"inline"` trails the text on the same row — right for a wide hint with a
	 * short action. `"below"` stacks it under the text, which is what a narrow column (a form-field
	 * width) needs, since a trailing button there squeezes the text into a ragged ribbon.
	 */
	actionPlacement?: "inline" | "below";
	/**
	 * Opt into a hint that comes and *goes*, instead of one rendered by a `&&` that appears once.
	 *
	 * The plain conditional form is right for a hint that shows up and stays (a picked role explains
	 * itself). It is wrong for one the user can dismiss or undo, because the entrance below animates
	 * opacity while the hint's HEIGHT arrives instantly — so everything under it jumps by the hint's
	 * full height at frame one, and the fade is read as a stutter rather than an entrance. Going away
	 * is worse: a `&&` flipping false is a hard cut with no exit at all.
	 *
	 * Passing `open` collapses the height in step with the fade and gives the hint a real exit. Keep
	 * rendering it unconditionally and let this drive it — a `&&` around it would unmount the element
	 * before the exit could run, which is the bug this exists to avoid.
	 */
	open?: boolean;
	dataTestId?: string;
	/**
	 * A walkthrough anchor name. Placed on the note itself, not on the `Collapse` around a controlled hint, so
	 * the anchor exists exactly while the note is mounted — an `open` hint that is closed has none.
	 */
	"data-annie-target"?: string;
	sx?: SxProps<Theme>;
};

// Enter only — a hint appears in response to a change, and never needs to animate away. Transform and
// opacity only (no layout properties), and dropped entirely under reduced-motion.
const ENTRANCE_SX: SxProps<Theme> = {
	"@keyframes inlineHintEnter": {
		from: { opacity: 0, transform: "translateY(-4px)" },
		to: { opacity: 1, transform: "translateY(0)" },
	},
	animation: `inlineHintEnter ${MOTION.DURATION.ENTRANCE_SM}ms ${MOTION.EASE_OUT}`,
	"@media (prefers-reduced-motion: reduce)": { animation: "none" },
};

/**
 * Inline informational note attached to a form field or section — a tinted soft surface with a
 * leading status glyph and a line or two of text.
 *
 * The *visible* counterpart to `InfoIconWithTooltip`: reach for this when the message has to be
 * read, and for the tooltip when it only has to be available on demand.
 */
export const InlineHint: FC<Props> = ({
	children,
	contentComponent = "p",
	tone = "info",
	icon,
	hideIcon = false,
	action,
	actionPlacement = "inline",
	open,
	dataTestId,
	"data-annie-target": annieTarget,
	sx,
}) => {
	const isControlled = open !== undefined;

	const hint = (
		<Box
			data-testid={dataTestId}
			data-annie-target={annieTarget}
			// Announced when it appears after a change (e.g. picking a role) without stealing focus from
			// the control the user is currently operating.
			role="status"
			aria-live="polite"
			sx={[
				ENTRANCE_SX,
				// A height that collapses while the ink stays fully opaque reads as the note being guillotined,
				// so the controlled form fades on the way out too. Enter needs nothing here — the keyframe
				// above already fades in, and it runs on mount, which is when `Collapse` mounts this.
				isControlled && {
					opacity: open ? 1 : 0,
					transition: `opacity ${MOTION.DURATION.INLINE_EXIT}ms ${MOTION.EASE_OUT}`,
					"@media (prefers-reduced-motion: reduce)": { transition: "none" },
				},
				(theme: Theme) => {
					const accent = tone === "neutral" ? theme.palette.text.secondary : theme.palette[tone].main;
					const ink = tone === "neutral" ? theme.palette.text.secondary : theme.palette[tone].dark;
					return {
						display: "flex",
						alignItems: "flex-start",
						gap: 1,
						px: 1.25,
						py: 1,
						borderRadius: 1,
						backgroundColor: alpha(accent, ALPHA.HINT_TINT),
						color: ink,
						"& > svg": { fontSize: FONT_SIZE.EMPHASIS, mt: "2px", flexShrink: 0 },
						// An `action` button keeps its own semantics but adopts the hint's ink and scale, so it
						// reads as part of the note rather than a raised control dropped onto the tint. Styled
						// here rather than at each call site so every hint's action looks the same.
						"& .MuiButtonBase-root": {
							alignSelf: "flex-start",
							minHeight: 0,
							px: 1,
							py: 0.25,
							color: "inherit",
							fontSize: FONT_SIZE.SECONDARY,
							fontWeight: 600,
							backgroundColor: "transparent",
							"&:hover": { backgroundColor: alpha(accent, ALPHA.HINT_TINT), textDecoration: "underline" },
						},
					};
				},
				...(Array.isArray(sx) ? sx : [sx]),
			]}
		>
			{!hideIcon && (icon ?? <InfoOutlined fontSize="inherit" aria-hidden />)}
			<Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 0.5, flex: 1, minWidth: 0 }}>
				<Typography component={contentComponent} sx={{ fontSize: FONT_SIZE.SECONDARY, lineHeight: 1.45 }}>
					{children}
				</Typography>
				{action && actionPlacement === "below" && action}
			</Box>
			{action && actionPlacement === "inline" && action}
		</Box>
	);

	if (!isControlled) return hint;
	return (
		// `unmountOnExit` so a closed hint costs no live region: `aria-live` on a present-but-collapsed
		// node can still be announced, which would read the note out after the user dismissed it.
		<Collapse
			in={open}
			unmountOnExit
			timeout={{ enter: MOTION.DURATION.ENTRANCE_SM, exit: MOTION.DURATION.INLINE_EXIT }}
			// The media query, not the `timeout` prop, is what actually drops the motion. `timeout` is
			// react-transition-group's unmount DELAY, and MUI writes the height transition as an inline
			// style regardless — so driving it from a JS reduced-motion hook left the height animating at
			// full duration while the fade above had already been dropped (measured, not assumed). Killing
			// the transition here makes the height snap; the leftover delay is invisible at zero height.
			sx={{ "@media (prefers-reduced-motion: reduce)": { transition: "none" } }}
		>
			{hint}
		</Collapse>
	);
};
